/**
 * AgentTeam 适配器函数
 *
 * 将 AgentConversationMessage 格式转换为 AI Elements 格式，
 * 以实现与 ai-elements 组件的无缝集成。
 */

import type { AIElementsMessageProps, AIElementsMessagePart } from './chatAdapter';
import {
  streamingPartsToAIElements,
} from './chatAdapter';
import type { AgentConversationMessage, ConversationRole } from '../types/agentTeam';
import type { StreamingMessagePart } from '../types/chat';

const ROLE_AVATAR_MAP: Record<ConversationRole, string> = {
  user: '',
  orchestrator: 'https://api.dicebear.com/7.x/bottts/svg?seed=orchestrator',
  executor: 'https://api.dicebear.com/7.x/bottts/svg?seed=executor',
  system: '',
  planner: 'https://api.dicebear.com/7.x/bottts/svg?seed=planner',
};

export const ROLE_DISPLAY_MAP: Record<ConversationRole, string> = {
  user: '用户',
  orchestrator: '编排者',
  executor: '执行者',
  system: '系统',
  planner: '规划者',
};

export const ROLE_EMOJI_MAP: Record<ConversationRole, string> = {
  user: '👤',
  orchestrator: '🎯',
  executor: '⚙️',
  system: '📢',
  planner: '📋',
};

function mapRoleToAIElements(role: ConversationRole): 'user' | 'assistant' | 'system' {
  if (role === 'user') return 'user';
  if (role === 'system') return 'system';
  return 'assistant';
}

function generateMessageId(message: AgentConversationMessage): string {
  return `agent-msg-${message.id}`;
}

function getAvatar(role: ConversationRole): string | undefined {
  if (role === 'user') return undefined;
  return ROLE_AVATAR_MAP[role];
}

export interface AgentTeamMessageProps extends AIElementsMessageProps {
  agentRole?: ConversationRole;
  speakerName?: string;
  roleEmoji?: string;
  isLoading?: boolean;
}

export function toAIElementsMessage(msg: AgentConversationMessage): AgentTeamMessageProps {
  const role = mapRoleToAIElements(msg.role);
  const avatar = getAvatar(msg.role);
  const createdAt = msg.created_at ? new Date(msg.created_at).getTime() : Date.now();

  let parts: AIElementsMessagePart[] = [];

  const msgData = msg.data as Record<string, unknown> | null;
  const rawParts = (msgData?.parts || []) as StreamingMessagePart[];
  if (rawParts.length > 0) {
    const aiElements = streamingPartsToAIElements({
      parts: rawParts,
      isComplete: true,
    });
    for (const elem of aiElements) {
      if (elem.parts) {
        parts.push(...elem.parts);
      } else if (elem.toolCalls) {
        for (const tc of elem.toolCalls) {
          parts.push({
            type: 'tool',
            toolCallId: tc.toolCallId || '',
            toolName: tc.toolName,
            status: tc.status,
            args: tc.args,
            result: tc.result,
            error: tc.error,
            timestamp: tc.timestamp,
          });
        }
      }
    }
  }

  if (parts.length === 0) {
    parts = [{ type: 'text', content: msg.content || '' }];
  }

  return {
    id: generateMessageId(msg),
    type: 'text',
    role,
    content: msg.content || '',
    parts,
    createdAt,
    hasTime: true,
    avatar,
    agentRole: msg.role,
    speakerName: msg.speaker_name || ROLE_DISPLAY_MAP[msg.role],
    roleEmoji: ROLE_EMOJI_MAP[msg.role],
  };
}

export function toAIElementsMessages(messages: AgentConversationMessage[]): AgentTeamMessageProps[] {
  return messages.map(toAIElementsMessage);
}

export function streamingToAIElements(
  streamingContent: string,
  streamingRole: ConversationRole,
  streamingSenderName?: string,
  streamingParts?: StreamingMessagePart[],
): AgentTeamMessageProps {
  const role = mapRoleToAIElements(streamingRole);
  const avatar = getAvatar(streamingRole);
  const createdAt = Date.now();

  let parts: AIElementsMessagePart[] = [];

  if (streamingParts && streamingParts.length > 0) {
    const aiElements = streamingPartsToAIElements({
      parts: streamingParts,
      isComplete: false,
    });
    for (const elem of aiElements) {
      if (elem.parts) {
        parts.push(...elem.parts);
      } else if (elem.toolCalls) {
        for (const tc of elem.toolCalls) {
          parts.push({
            type: 'tool',
            toolCallId: tc.toolCallId || '',
            toolName: tc.toolName,
            status: tc.status,
            args: tc.args,
            result: tc.result,
            error: tc.error,
            timestamp: tc.timestamp,
          });
        }
      }
    }
  }

  if (parts.length === 0) {
    parts = [{ type: 'text', content: streamingContent || '' }];
  }

  return {
    id: 'streaming',
    type: 'text',
    role,
    content: streamingContent || '',
    parts,
    createdAt,
    hasTime: true,
    avatar,
    isLoading: true,
    agentRole: streamingRole,
    speakerName: streamingSenderName || ROLE_DISPLAY_MAP[streamingRole],
    roleEmoji: ROLE_EMOJI_MAP[streamingRole],
  };
}

export const agentTeamAdapter = {
  toAIElementsMessage,
  toAIElementsMessages,
  streamingToAIElements,
  ROLE_DISPLAY_MAP,
  ROLE_EMOJI_MAP,
};

export default agentTeamAdapter;